import React, { useState } from 'react';
import { Button, Dropdown, MenuProps, Tag, Tooltip } from 'antd';
import {
    AppleFilled,
    DeleteOutlined,
    FieldTimeOutlined,
    GoogleOutlined,
    MoreOutlined,
    PlayCircleOutlined,
    StopOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import {
    WalletCredential,
    canResumeWalletCredential,
    canRevokeWalletCredential,
    canSuspendWalletCredential,
    getWalletCredentialKey,
    getWalletPlatformLabel,
    getWalletStatusLabel,
    getWalletStatusText,
    getWalletSuspendSourceLabel,
} from './walletCredentials';

type WalletCredentialListProps = {
    credentials: WalletCredential[];
    actionsEnabled?: boolean;
    showTimestamps?: boolean;
    maxVisible?: number;
    onSuspend?: (credential: WalletCredential) => void;
    onResume?: (credential: WalletCredential) => void;
    onRevoke?: (credential: WalletCredential) => void;
};

const shortPassId = (passId?: string) => passId
    ? `Pass …${passId.slice(-8)}`
    : 'Awaiting HID pass details';

const getCredentialMeta = (credential: WalletCredential) => {
    const cardLabel = credential.cardNumber
        ? `Card ${credential.cardNumber}`
        : shortPassId(credential.passId);
    const normalizedStatus = `${credential.status || ''}`.toUpperCase();
    const suspendSource = ['SUSPENDING', 'SUSPENDED'].includes(normalizedStatus)
        ? getWalletSuspendSourceLabel(credential)
        : undefined;
    return [cardLabel, suspendSource].filter(Boolean).join(' · ');
};

const getStatusTagColor = (status?: string) => {
    switch (`${status || ''}`.toUpperCase()) {
        case 'ACTIVE':
            return 'green';
        case 'SUSPENDING':
        case 'SUSPENDED':
            return 'gold';
        case 'RESUMING':
        case 'PROVISIONING':
        case 'ISSUANCE_CREATED':
            return 'blue';
        case 'REVOKING':
        case 'REVOKED':
        case 'FAILED':
            return 'red';
        default:
            return undefined;
    }
};

const getDeviceIcon = (credential: WalletCredential) => {
    const platform = `${credential.platform || ''}`.toUpperCase();
    const deviceType = `${credential.deviceType || ''}`.toUpperCase();
    if (platform === 'APPLE' && deviceType === 'WATCH') return <FieldTimeOutlined />;
    if (platform === 'APPLE') return <AppleFilled />;
    if (platform === 'GOOGLE') return <GoogleOutlined />;
    return <WalletOutlined />;
};

const getDeviceIconClass = (credential: WalletCredential) => {
    const platform = `${credential.platform || ''}`.toUpperCase();
    if (platform === 'APPLE') return 'wallet-credential__icon--apple';
    if (platform === 'GOOGLE') return 'wallet-credential__icon--google';
    return 'wallet-credential__icon--default';
};

const WalletCredentialList = ({
    credentials,
    actionsEnabled = false,
    showTimestamps = false,
    maxVisible,
    onSuspend,
    onResume,
    onRevoke,
}: WalletCredentialListProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasCollapsedCredentials = !!maxVisible && credentials.length > maxVisible;
    const visibleCredentials = hasCollapsedCredentials && !isExpanded
        ? credentials.slice(0, maxVisible)
        : credentials;
    const hiddenCredentialCount = credentials.length - visibleCredentials.length;

    const renderActions = (credential: WalletCredential) => {
        const canSuspend = canSuspendWalletCredential(credential);
        const canResume = canResumeWalletCredential(credential);
        const canRevoke = canRevokeWalletCredential(credential);
        if (!actionsEnabled || (!canSuspend && !canResume && !canRevoke)) return null;

        const deviceLabel = getWalletPlatformLabel(credential.platform, credential.deviceType);
        const items: NonNullable<MenuProps['items']> = [];
        if (canSuspend) items.push({ key: 'suspend', icon: <StopOutlined />, label: 'Suspend' });
        if (canResume) items.push({ key: 'resume', icon: <PlayCircleOutlined />, label: 'Resume' });
        if (canRevoke) items.push({ key: 'revoke', danger: true, icon: <DeleteOutlined />, label: 'Revoke' });

        return (
            <Dropdown
                menu={{
                    className: 'wallet-credential-menu',
                    items,
                    onClick: ({ key, domEvent }) => {
                        domEvent.stopPropagation();
                        if (key === 'suspend') onSuspend?.(credential);
                        if (key === 'resume') onResume?.(credential);
                        if (key === 'revoke') onRevoke?.(credential);
                    },
                }}
                trigger={['click']}
                placement="bottomRight"
                destroyPopupOnHide
            >
                <Button
                    type="text"
                    shape="circle"
                    size="small"
                    className="wallet-credential__action"
                    icon={<MoreOutlined />}
                    aria-label={`Actions for ${deviceLabel}`}
                    onClick={(event) => event.stopPropagation()}
                />
            </Dropdown>
        );
    };

    return (
        <div className={`wallet-credential-list${showTimestamps ? ' wallet-credential-list--detailed' : ''}`}>
            {visibleCredentials.map((credential, index) => {
                const key = getWalletCredentialKey(credential, index);
                const deviceLabel = getWalletPlatformLabel(credential.platform, credential.deviceType, credential.status);
                return (
                    <div
                        key={key}
                        data-testid={`wallet-credential-${key}`}
                        className="wallet-credential"
                    >
                        <div className={`wallet-credential__icon ${getDeviceIconClass(credential)}`} aria-hidden="true">
                            {getDeviceIcon(credential)}
                        </div>
                        <div className="wallet-credential__content">
                            <div className="wallet-credential__heading">
                                <Tooltip title={deviceLabel} mouseEnterDelay={0.5}>
                                    <span className="wallet-credential__device">{deviceLabel}</span>
                                </Tooltip>
                                <Tooltip title={getWalletStatusText(credential)} mouseEnterDelay={0.5}>
                                    <Tag color={getStatusTagColor(credential.status)}>
                                        {getWalletStatusLabel(credential.status)}
                                    </Tag>
                                </Tooltip>
                            </div>
                            <div className="wallet-credential__meta">{getCredentialMeta(credential)}</div>
                            {showTimestamps && (credential.updatedAt || credential.createdAt) && (
                                <div className="wallet-credential__timestamp">
                                    Updated {new Date(credential.updatedAt || credential.createdAt!).toLocaleString()}
                                </div>
                            )}
                        </div>
                        <div className="wallet-credential__actions">
                            {renderActions(credential)}
                        </div>
                    </div>
                );
            })}
            {hasCollapsedCredentials && (
                <Button
                    type="link"
                    size="small"
                    className="wallet-credential-list__more"
                    aria-expanded={isExpanded}
                    onClick={(event) => {
                        event.stopPropagation();
                        setIsExpanded((expanded) => !expanded);
                    }}
                >
                    {isExpanded ? 'Show less' : `+${hiddenCredentialCount} more`}
                </Button>
            )}
        </div>
    );
};

export default WalletCredentialList;
